'use client';

import Link from 'next/link';
import { format } from 'date-fns';
import { BlocksRenderer } from './blocks';
import type { PageBlock } from '@impact/types';

export interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  category: string | null;
  content: string | null;
  blocks: PageBlock[] | null;
  featuredImageUrl: string | null;
  isPublished: boolean;
  publishedAt: string | null;
}

interface BlogPostPageProps {
  post: BlogPostData;
  homePath?: string;
  listPath?: string;
}

export function BlogPostPage({ post, homePath = '/', listPath = '/blog' }: BlogPostPageProps) {
  const hasBlocks = Array.isArray(post.blocks) && post.blocks.length > 0;

  return (
    <article className="min-h-screen bg-background">
      {post.featuredImageUrl ? (
        <section className="relative h-[32vh] min-h-[260px] md:h-[45vh] md:min-h-[360px] bg-zinc-900">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={post.featuredImageUrl}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="relative z-10 h-full hidden md:flex items-end">
            <div className="container pb-8 md:pb-12">
              <h1 className="text-3xl md:text-5xl font-bold text-white">{post.title}</h1>
            </div>
          </div>
        </section>
      ) : null}

      <div className="container py-10 md:py-14">
        <div className="mx-auto max-w-3xl">
          <nav className="mb-4 text-sm text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link href={homePath} className="hover:text-foreground">
                  Home
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link href={listPath} className="hover:text-foreground">
                  Blog
                </Link>
              </li>
              <li>/</li>
              <li className="text-foreground">{post.title}</li>
            </ol>
          </nav>
          {post.featuredImageUrl ? (
            <h1 className="text-3xl md:text-5xl font-bold md:hidden">{post.title}</h1>
          ) : (
            <h1 className="text-3xl md:text-5xl font-bold">{post.title}</h1>
          )}
          <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
            {post.category ? (
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
                {post.category}
              </span>
            ) : null}
            {post.publishedAt ? (
              <span>{format(new Date(post.publishedAt), 'MMMM d, yyyy')}</span>
            ) : null}
          </div>

          <div className="mt-8">
            {hasBlocks ? (
              <BlocksRenderer blocks={post.blocks!} />
            ) : (
              <div
                className="prose prose-zinc max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: post.content || '' }}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
